import dateFormat from "dateformat";
import PropTypes from "prop-types";
import React, { useEffect } from "react";
import { useSearchParams } from "react-router-dom";
import { Cart, Stay } from "rsl-api-106/lib/types";

import { useSession } from "@hooks";
import { sendDataLayer, sendDataLayerPurchase } from "@utils/tracking/gtm";

type GTMConfirmationPageProps = {
  cart: Cart;
};

const iterateProduct = (cart: Cart): any[] => {
  let products: any[] = [];
  cart.reservations!![0]?.stays?.forEach((stay) => {
    let productBasePrice = "0";
    stay?.occupants?.forEach((occupant) => {
      occupant?.lineItems?.forEach((lineitem) => {
        if (lineitem?.product?.isBase) {
          productBasePrice =
            lineitem.product.price?.amount?.toFixed(2).toString() ?? "0";
        }
      });
    });

    products = [
      ...products,
      {
        name: cart.reservations!![0]?.property?.label,
        id: cart.reservations!![0]?.property?.id,
        price: productBasePrice,
        brand: "Vacanceole",
        category: stay?.distribution?.roomTypes?.join(" , "),
        quantity: "1",
        formule: getBaseProduct(cart),
        participant: stay?.occupants?.length,
        duree_sejour: stay?.nbDays,
        date_arrivee: stay?.startDate,
        date_depart: stay?.endDate,
      },
    ];
  });
  return products;
};

const iterateCriteoItems = (cart: Cart): any[] => {
  let items: any[] = [];
  cart.reservations!![0]?.stays?.forEach((stay) => {
    let productBasePrice = "0";
    stay?.occupants?.forEach((occupant) => {
      occupant?.lineItems?.forEach((lineitem) => {
        if (lineitem?.product?.isBase) {
          productBasePrice =
            lineitem.product.price?.amount?.toFixed(2).toString() ?? "0";
        }
      });
    });

    items = [
      ...items,
      {
        id: cart.reservations!![0]?.property?.id,
        price: productBasePrice,
        quantity: stay?.distribution?.roomTypes?.reduce((acc, current) => {
          return acc + (current?.quantity ? current?.quantity : 0);
        }, 0),
      },
    ];
  });

  return items;
};

const getBaseProduct = (cart: Cart): string => {
  let code: string[] = [];
  cart?.reservations?.forEach((resa) => {
    resa?.stays?.forEach((stay) => {
      stay?.occupants?.forEach((occupant) => {
        occupant?.lineItems?.forEach((lineitem) => {
          if (lineitem?.product?.isBase) {
            code = [...code, lineitem?.product?.label!!];
          }
        });
      });
    });
  });
  return code.join(" , ");
};

const GTMConfirmationPage: React.FC<GTMConfirmationPageProps> = ({ cart }) => {
  /* Hooks */
  const { lang, funnel_type } = useSession();
  const [searchParams, setSearchParams] = useSearchParams();

  const handleGetGTMItems = () => {
    let items: any = [];
    cart?.reservations?.forEach((r) => {
      r?.stays?.forEach((s) => {
        items = [
          ...items,
          {
            currency: s?.totalAmount?.currencyCode,
            item_id: r.property?.id,
            item_name: r.property?.label,
            item_category: s?.distribution?.roomTypes
              ?.map((x) => x?.label)
              .join(","),
            item_category2: "VACANCEOLE location de particulier", //ou location de particulier ou partenaire
            item_brand: "Vacanceole",
            item_variant: getFormuleSejour(s!!), //Formule location – Studio 2 personnes - Twin
            price: s?.totalAmount?.amount?.toFixed(2),
            quantity: handleGetNbRoom(s!!), //ATTENTION à DYNAMISER EN FONCTION DU CHOIX
          },
        ];
      });
    });

    return items;
  };

  const handleGetNbRoom = (stay: Stay) => {
    let n = 0;

    stay?.distribution?.roomTypes?.forEach((ri) => {
      n += ri?.quantity ?? 0;
    });

    return n;
  };

  const getFormuleSejour = (stay: Stay) => {
    let s = "";
    stay.occupants?.forEach((o) => {
      o?.lineItems?.forEach((l) => {
        if (l?.product?.isBase) {
          s = l.product.label!!;
        }
      });
    });

    return s;
  };

  const handleGetTotalOccupant = () => {
    let n = 0;
    cart.reservations?.forEach((r) => {
      r?.stays?.forEach((s) => {
        n += s?.occupants?.length ?? 0;
      });
    });

    return n;
  };

  const handleGetTotalDuration = () => {
    let n = 0;
    cart.reservations?.forEach((r) => {
      r?.stays?.forEach((s) => {
        n += s?.nbDays ?? 0;
      });
    });

    return n;
  };

  useEffect(() => {
    /*sendDataLayer({
      event: "view_page",
      page: {
        page_path: window.location.href, //https://www.vacanceole.com/		"page_title": "$_META_TITLE",//Vacancéole : Location Vacances XXXXX
        pagename: window.location.pathname, //= pagename
        typepage: "Confirmation", // = reprise du nom de l’étape (nom technique par exemple)
        langue: lang, // = langue
        site: "vacancecole", // = site
        webuser:
          searchParams.get("webuser") ??
          searchParams.get("wu") ??
          process.env.REACT_APP_WEBUSER!!, //webuser
        isCustomerArea: searchParams.get("ca") ? true : false, //boolean a true si on arrive en modification de dossier depuis l'espace client - sinon false
      },
    });

    const items = handleGetGTMItems();
    const totalOccupant = handleGetTotalOccupant();
    const duration = handleGetTotalDuration();
    const paymentMethod = "CB";

    sendDataLayer({
      event: "purchase",
      participant: totalOccupant,
      duree_sejour: duration,
      date_arrivee: dateFormat(
        new Date(cart?.reservations!![0]?.startDate),
        "ddmmyyyy"
      ),
      date_depart: dateFormat(
        new Date(cart?.reservations!![0]?.endDate),
        "ddmmyyyy"
      ),
      //isEditingResa: cart?.reservations?.[0]?.id ? true : false,
      webuser:
        searchParams.get("webuser") ??
        searchParams.get("wu") ??
        process.env.REACT_APP_WEBUSER!!, //webuser
      isCustomerArea: searchParams.get("ca") ? true : false, //boolean a true si on arrive en modification de dossier depuis l'espace client - sinon false
      ecommerce: {
        transaction_id: cart.reservations!![0]?.id,
        affiliation: "Online - " + paymentMethod,
        value: cart.reservations!![0]?.totalAmount?.amount?.toFixed(2),
        tax: "",
        shipping: "0", //champ obligatoire laissez à 0
        coupon: cart.reservations!![0]?.discountCode, //si existe sinon ""
        code_fidelite: cart.reservations!![0]?.discountCode, //si exisite sinon ""
        items: items,
      },
    });

    sendDataLayer({
      typepage: "confirmation",
      pagename: "confirmation_commande",
      connexion: "1",
      transaction_total:
        cart.reservations!![0]?.totalAmount?.amount?.toFixed(2),
      transaction_type: cart.reservations!![0]?.selectedPaymentDue,
      idclient: "",
      langue: lang.toLowerCase(),
      site: "vacancéole",
      webuser:
        searchParams.get("webuser") ??
        searchParams.get("wu") ??
        process.env.REACT_APP_WEBUSER!!, //webuser
      isCustomerArea: searchParams.get("ca") ? true : false, //boolean a true si on arrive en modification de dossier depuis l'espace client - sinon false
      ecommerce: {
        purchase: {
          actionField: {
            step: "4",
            id: cart.reservations!![0]?.id,
            revenue: cart.reservations!![0]?.totalAmount?.amount?.toFixed(2),
            shipping: "0",
            coupon: cart.reservations!![0]?.discountCode,
          },
          products: iterateProduct(cart),
        },
      },
      criteo_items: iterateCriteoItems(cart),
    });*/

    sendDataLayerPurchase(cart, funnel_type);
  }, []);

  return <></>;
};

GTMConfirmationPage.propTypes = {
  //
};

export default GTMConfirmationPage;
